import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Chip,
  Button,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { leadFlagsAPI } from '../../services/api';

const LeadFlags = ({ jobId, leadId, onFlagsChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newFlagName, setNewFlagName] = useState('');
  const [newFlagColor, setNewFlagColor] = useState('#1976d2');
  const [isGlobal, setIsGlobal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch available flags
  const { data: availableFlags } = useQuery(['flags'], leadFlagsAPI.getFlags);

  // Fetch flags assigned to this lead
  const { data: leadFlags, isLoading } = useQuery(
    ['leadFlags', jobId, leadId],
    () => leadFlagsAPI.getLeadFlags(jobId, leadId),
    {
      onSuccess: (data) => {
        if (onFlagsChange) {
          onFlagsChange(data);
        }
      },
    }
  );

  const assignFlagMutation = useMutation(
    (flagId) => leadFlagsAPI.assignFlag(jobId, leadId, flagId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['leadFlags', jobId, leadId]);
        queryClient.invalidateQueries(['jobLeads', jobId]); // Invalidate leads list
        queryClient.invalidateQueries(['jobLeadsAll', jobId]); // Invalidate all leads
        setAnchorEl(null);
      },
    }
  );

  const removeFlagMutation = useMutation(
    (flagId) => leadFlagsAPI.removeFlag(jobId, leadId, flagId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['leadFlags', jobId, leadId]);
        queryClient.invalidateQueries(['jobLeads', jobId]); // Invalidate leads list
        queryClient.invalidateQueries(['jobLeadsAll', jobId]); // Invalidate all leads
      },
    }
  );

  const createFlagMutation = useMutation(
    (flagData) => leadFlagsAPI.createFlag(flagData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['flags']);
        setCreateDialogOpen(false);
        setNewFlagName('');
        setNewFlagColor('#1976d2');
        setIsGlobal(false);
      },
    }
  );

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleAssignFlag = (flagId) => {
    assignFlagMutation.mutate(flagId);
  };

  const handleRemoveFlag = (flagId) => {
    if (window.confirm('Remove this flag from the lead?')) {
      removeFlagMutation.mutate(flagId);
    }
  };

  const handleCreateFlag = () => {
    if (!newFlagName.trim()) {
      alert('Please enter a flag name');
      return;
    }

    createFlagMutation.mutate({
      name: newFlagName,
      color: newFlagColor,
      is_global: isGlobal,
    });
  };

  // Get assigned flag IDs for filtering
  const assignedFlagIds = leadFlags ? leadFlags.map(lf => lf.flag.id) : [];

  // Filter out already assigned flags from menu
  const unassignedFlags = availableFlags
    ? availableFlags.filter(flag => !assignedFlagIds.includes(flag.id))
    : [];

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      {/* Display assigned flags */}
      {leadFlags && leadFlags.map((leadFlag) => (
        <Chip
          key={leadFlag.id}
          label={leadFlag.flag.name}
          onDelete={() => handleRemoveFlag(leadFlag.flag.id)}
          sx={{
            bgcolor: leadFlag.flag.color,
            color: '#fff',
            '& .MuiChip-deleteIcon': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: '#fff',
              },
            },
          }}
        />
      ))}

      {/* Add Flag Button */}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleOpenMenu}
        variant="outlined"
      >
        Add Flag
      </Button>

      {/* Flag Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {unassignedFlags.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              No available flags
            </Typography>
          </MenuItem>
        )}
        {unassignedFlags.map((flag) => (
          <MenuItem key={flag.id} onClick={() => handleAssignFlag(flag.id)}>
            <Chip
              label={flag.name}
              size="small"
              sx={{
                bgcolor: flag.color,
                color: '#fff',
              }}
            />
          </MenuItem>
        ))}
        <MenuItem onClick={() => { handleCloseMenu(); setCreateDialogOpen(true); }}>
          <Typography variant="body2" color="primary">
            + Create New Flag
          </Typography>
        </MenuItem>
      </Menu>

      {/* Create Flag Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Flag</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Flag Name"
            value={newFlagName}
            onChange={(e) => setNewFlagName(e.target.value)}
            margin="normal"
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              Color:
            </Typography>
            <input
              type="color"
              value={newFlagColor}
              onChange={(e) => setNewFlagColor(e.target.value)}
              style={{ width: '100%', height: 40, cursor: 'pointer' }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
              />
            }
            label="Make this flag available to all users (Global)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateFlag}
            disabled={createFlagMutation.isLoading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadFlags;

